import { differenceInMilliseconds, isBefore } from 'date-fns';
import { action, makeObservable, observable } from 'mobx';
import type { UnitQueueRowData } from '../socket/stream-types';

type ElapsingTimer = {
    state: 'ELAPSING';
    timeoutID: ReturnType<typeof setTimeout>;
    timerStart: string;
    timerEnd: string;
};

export type UnitTimer =
    | {
          state: 'NO_TIMER';
      }
    | ElapsingTimer
    | {
          state: 'ALERTING';
      };

type UnitTimerMap = { [unitId: number]: UnitTimer | undefined };

export class UnitTimersStore {
    @observable
    private unitTimerMap: UnitTimerMap = {};
    private playSound: (unitId: number) => void;
    private shouldPlaySound: boolean = true;
    constructor(playSound: (unitId: number) => void) {
        this.playSound = playSound;
        makeObservable(this);
    }

    @action
    public refreshTimer(
        unitQueueView: UnitQueueRowData,
        shouldPlaySoundRightNow: boolean = true
    ): void {
        const unitId = unitQueueView.id;
        let enablePlaySoundAtEnd = false;

        // We'll just always create a timer if one doesn't exist since it's very cheap to do.
        if (!this.unitTimerMap[unitId]) {
            this.unitTimerMap[unitId] = {
                state: 'NO_TIMER',
            };
        }

        // If this.shouldPlaySound is true, but we don't want to play the sound right now (in this function run),
        // then we disable it now, but enable it again in the end of the function to not mess with the sound proper working.
        if (this.shouldPlaySound && !shouldPlaySoundRightNow) {
            enablePlaySoundAtEnd = true;
            this.disablePlaySound();
        }

        if (unitQueueView.timerEnd) {
            // If the timer end is after now, create an alerting timer and return early
            if (isBefore(new Date(unitQueueView.timerEnd), new Date())) {
                this.transitionToAlerting(unitQueueView);

                if (enablePlaySoundAtEnd) {
                    this.enablePlaySound();
                }

                return;
            }
            const unitTimer = this.unitTimerMap[unitId];
            if (
                unitTimer?.state !== 'ELAPSING' ||
                (unitTimer.state === 'ELAPSING' && unitQueueView.timerEnd !== unitTimer.timerEnd)
            ) {
                this.transitionToElapsing(unitQueueView);
            }
        } else {
            if (this.unitTimerMap[unitId]?.state !== 'NO_TIMER') {
                this.transitionToNoTimer(unitQueueView);
            }
        }

        if (enablePlaySoundAtEnd) {
            this.enablePlaySound();
        }
    }

    public getUnitTimer(unitId: number): UnitTimer | undefined {
        return this.unitTimerMap[unitId];
    }

    public disablePlaySound() {
        this.shouldPlaySound = false;
    }

    public enablePlaySound() {
        this.shouldPlaySound = true;
    }

    private maybeClearTimer(unitId: number): void {
        const unitTimer = this.unitTimerMap[unitId];
        if (unitTimer?.state === 'ELAPSING') {
            clearTimeout(unitTimer.timeoutID);
        }
    }

    @action
    private transitionToNoTimer(unitQueueView: UnitQueueRowData): void {
        this.maybeClearTimer(unitQueueView.id);
        this.unitTimerMap[unitQueueView.id] = {
            state: 'NO_TIMER',
        };
    }

    @action
    private transitionToElapsing(unitQueueView: UnitQueueRowData): void {
        this.maybeClearTimer(unitQueueView.id);
        const difference = differenceInMilliseconds(
            new Date(unitQueueView.timerEnd ?? ''),
            new Date()
        );
        const timeoutID = setTimeout(() => {
            this.transitionToAlerting(unitQueueView);
        }, difference);
        this.unitTimerMap[unitQueueView.id] = {
            state: 'ELAPSING',
            timerStart: unitQueueView.timerStart ?? '',
            timerEnd: unitQueueView.timerEnd ?? '',
            timeoutID,
        };
    }

    @action
    private transitionToAlerting(unitQueueView: UnitQueueRowData): void {
        this.maybeClearTimer(unitQueueView.id);
        if (this.shouldPlaySound) {
            this.playSound(unitQueueView.id);
        }
        this.unitTimerMap[unitQueueView.id] = {
            state: 'ALERTING',
        };
    }
}
